import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import AppLayout from "@/components/layout/AppLayout";
import Dashboard from "@/pages/Dashboard";
import InvoicingDashboard from "@/pages/invoicing/InvoicingDashboard";
import InvoiceForm from "@/pages/invoicing/InvoiceForm";
import InvoiceSettingsPage from "@/pages/invoicing/InvoiceSettings";
import CRMDashboard from "@/pages/crm/CRMDashboard";
import CustomerForm from "@/pages/crm/CustomerForm";
import AccountingDashboard from "@/pages/accounting/AccountingDashboard";
import HRDashboard from "@/pages/hr/HRDashboard";
import InventoryDashboard from "@/pages/inventory/InventoryDashboard";
import RestaurantDashboard from "@/pages/restaurant/RestaurantDashboard";
import RestaurantPublicMenu from "@/pages/restaurant/RestaurantPublicMenu";
import RestaurantStaffLogin from "@/pages/restaurant/RestaurantStaffLogin";
import { RestaurantKitchen, RestaurantCashier, RestaurantWaiter } from "@/pages/restaurant/RestaurantStaffViews";
import AutoRepairDashboard from "@/pages/auto-repair/AutoRepairDashboard";
import FleetDashboard from "@/pages/fleet/FleetDashboard";
import LogisticsDashboard from "@/pages/logistics/LogisticsDashboard";
import TaxiDashboard from "@/pages/taxi/TaxiDashboard";
import CarRentalDashboard from "@/pages/car-rental/CarRentalDashboard";
import MoneyTransferDashboard from "@/pages/money-transfer/MoneyTransferDashboard";
import ReceiptsDashboard from "@/pages/receipts/ReceiptsDashboard";
import PublicReceiptScan from "@/pages/receipts/PublicReceiptScan";
import MeetingDashboard from "@/pages/meeting/MeetingDashboard";
import MeetingRoom from "@/pages/meeting/MeetingRoom";
import MeetingInviteRedirect from "@/pages/meeting/MeetingInviteRedirect";
import InvoiceItemsCatalog from "@/pages/invoicing/InvoiceItemsCatalog";
import ProjectsDashboard from "@/pages/projects/ProjectsDashboard";
import QuotesDashboard from "@/pages/quotes/QuotesDashboard";
import Settings from "@/pages/Settings";
import UsersAdmin from "@/pages/admin/UsersAdmin";
import PropagateAdmin from "@/pages/admin/PropagateAdmin";
import StaffProfile from "@/pages/admin/StaffProfile";
import LicensesAdmin from "@/pages/admin/LicensesAdmin";
import PartnerHubDashboard from "@/pages/partner-hub/PartnerHubDashboard";
import BIDashboard from "@/pages/bi/BIDashboard";
import CustomerPortalDashboard from "@/pages/customer-portal/CustomerPortalDashboard";
import UpsellPortalDashboard from "@/pages/upsell-portal/UpsellPortalDashboard";
import ReportsDashboard from "@/pages/reports/ReportsDashboard";
import StaffManagement from "@/pages/staff/StaffManagement";
import SecurityDashboard from "@/pages/SecurityDashboard";
import PayslipForm from "@/pages/payroll/PayslipForm";
import PayrollSettings from "@/pages/payroll/PayrollSettings";
import ShiftsDashboard from "@/pages/shifts/ShiftsDashboard";
import EmployeePortalLogin from "@/pages/employee-portal/EmployeePortalLogin";
import EmployeePortalLayout from "@/pages/employee-portal/EmployeePortalLayout";
import EmployeePortalDashboard from "@/pages/employee-portal/EmployeePortalDashboard";
import EmployeePortalShifts from "@/pages/employee-portal/EmployeePortalShifts";
import EmployeePortalPayslips from "@/pages/employee-portal/EmployeePortalPayslips";
import EmployeePortalProfile from "@/pages/employee-portal/EmployeePortalProfile";
import EmployeePortalChangePassword from "@/pages/employee-portal/EmployeePortalChangePassword";
import GenericModulePage from "@/pages/modules/GenericModulePage";
import { MODULE_DEFS } from "@/pages/modules/MODULE_DEFS";
import PlaceholderModule from "@/pages/modules/PlaceholderModule";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/scan/:token" element={<PublicReceiptScan />} />
          <Route path="/meet/:code" element={<MeetingRoom />} />
          <Route path="/meet/invite/:token" element={<MeetingInviteRedirect />} />

          {/* Julkinen Ravintolan QR-tilaussivu – asiakas skannaa pöydän QR-koodin */}
          <Route path="/r/:qrToken" element={<RestaurantPublicMenu />} />

          {/* Ravintola-moduulin OMA staff-kirjautuminen (erillinen Apps2Go-tunnuksista, Phase 20) */}
          <Route path="/restaurant/staff-login" element={<RestaurantStaffLogin />} />

          {/* Työntekijän self-service-portaali (oma auth) */}
          <Route path="/employee-portal/login" element={<EmployeePortalLogin />} />
          <Route path="/employee-portal/change-password" element={<EmployeePortalChangePassword />} />
          <Route path="/employee-portal" element={<EmployeePortalLayout />}>
            <Route index element={<EmployeePortalDashboard />} />
            <Route path="shifts" element={<EmployeePortalShifts />} />
            <Route path="payslips" element={<EmployeePortalPayslips />} />
            <Route path="profile" element={<EmployeePortalProfile />} />
          </Route>

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />

            {/* Implemented modules */}
            <Route path="invoicing" element={<InvoicingDashboard />} />
            <Route path="invoicing/items" element={<InvoiceItemsCatalog />} />
            <Route path="invoicing/new" element={<InvoiceForm />} />
            <Route path="invoicing/:id" element={<InvoiceForm />} />
            <Route path="invoicing/settings" element={<InvoiceSettingsPage />} />
            <Route path="crm" element={<CRMDashboard />} />
            <Route path="crm/new" element={<CustomerForm />} />
            <Route path="crm/:id" element={<CustomerForm />} />
            <Route path="accounting" element={<AccountingDashboard />} />
            <Route path="hr" element={<HRDashboard />} />
            <Route path="hr/settings" element={<PayrollSettings />} />
            <Route path="hr/payslip/new" element={<PayslipForm />} />
            <Route path="hr/payslip/:id" element={<PayslipForm />} />
            <Route path="shifts" element={<ShiftsDashboard />} />
            {/* Legacy redirects (entiset /payroll -reitit) */}
            <Route path="payroll" element={<Navigate to="/hr" replace />} />
            <Route path="payroll/settings" element={<Navigate to="/hr/settings" replace />} />
            <Route path="payroll/payslip/new" element={<Navigate to="/hr/payslip/new" replace />} />
            <Route path="payroll/payslip/:id" element={<PayslipForm />} />
            <Route path="inventory" element={<InventoryDashboard />} />
            <Route path="restaurant" element={<RestaurantDashboard />} />
            <Route path="restaurant/kitchen" element={<RestaurantKitchen />} />
            <Route path="restaurant/cashier" element={<RestaurantCashier />} />
            <Route path="restaurant/waiter" element={<RestaurantWaiter />} />
            <Route path="auto-repair" element={<AutoRepairDashboard />} />
            <Route path="fleet" element={<FleetDashboard />} />
            <Route path="logistics" element={<LogisticsDashboard />} />
            <Route path="settings" element={<Settings />} />

            {/* Admin only */}
            <Route path="admin/users" element={<UsersAdmin />} />
            <Route path="admin/staff/:id" element={<StaffProfile />} />
            <Route path="admin/propagate" element={<PropagateAdmin />} />
            <Route path="admin/licenses" element={<LicensesAdmin />} />

            {/* Liiketoiminta-laajennukset */}
            <Route path="partner-hub" element={<PartnerHubDashboard />} />
            <Route path="bi" element={<BIDashboard />} />
            <Route path="customer-portal" element={<CustomerPortalDashboard />} />
            <Route path="upsell-portal" element={<UpsellPortalDashboard />} />
            <Route path="finance-reports" element={<ReportsDashboard />} />
            <Route path="staff" element={<StaffManagement />} />
            <Route path="security" element={<SecurityDashboard />} />

            {/* Placeholder modules - coming soon */}
            <Route path="receipts" element={<ReceiptsDashboard />} />
            <Route path="projects" element={<ProjectsDashboard />} />
            <Route path="car-rental" element={<CarRentalDashboard />} />
            <Route path="taxi" element={<TaxiDashboard />} />
            <Route path="money-transfer" element={<MoneyTransferDashboard />} />
            <Route path="quotes" element={<QuotesDashboard />} />

            {/* 15 lisämoduulia ZIPistä – luotu geneerisellä module-page-pohjalla */}
            <Route path="advanced-crm" element={<GenericModulePage def={MODULE_DEFS.advanced_crm} />} />
            <Route path="cleaning" element={<GenericModulePage def={MODULE_DEFS.cleaning} />} />
            <Route path="construction" element={<GenericModulePage def={MODULE_DEFS.construction} />} />
            <Route path="dms" element={<GenericModulePage def={MODULE_DEFS.dms} />} />
            <Route path="events" element={<GenericModulePage def={MODULE_DEFS.event_management} />} />
            <Route path="field-service" element={<GenericModulePage def={MODULE_DEFS.field_service} />} />
            <Route path="food-beverage" element={<GenericModulePage def={MODULE_DEFS.food_beverage} />} />
            <Route path="home-care" element={<GenericModulePage def={MODULE_DEFS.home_care} />} />
            <Route path="meeting" element={<MeetingDashboard />} />
            <Route path="property" element={<GenericModulePage def={MODULE_DEFS.property_management} />} />
            <Route path="psa" element={<GenericModulePage def={MODULE_DEFS.psa} />} />
            <Route path="qms" element={<GenericModulePage def={MODULE_DEFS.qms} />} />
            <Route path="rental" element={<GenericModulePage def={MODULE_DEFS.rental} />} />
            <Route path="reporting" element={<GenericModulePage def={MODULE_DEFS.reporting} />} />
            <Route path="tasks" element={<GenericModulePage def={MODULE_DEFS.tasks} />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
